// One box that finds a ticket by number or by subject (docs/API.md §3.7).
//
// The agent types whatever they have — `#1234`, `1234`, or "card reader store 14" — and
// the server decides what it meant: a number resolves exactly first and then by prefix,
// anything else searches subjects. The picker only shows what came back and lets them
// choose; it never guesses on their behalf, because the write path refuses text for the
// same reason (`resolveTarget` in src/services/ticket-links.js). `excludeIds` keeps the
// ticket the box is on — and anything already linked or pending — out of the list.
//
// Debounced at 200ms and cancelled on every keystroke, so a fast typist costs one
// request, not one per letter, and a slow response can never paint over a newer one.

import { useEffect, useRef, useState } from 'react';

import { api } from '../../api/client.js';
import { StatusBadge } from './Badges.jsx';

const DEBOUNCE_MS = 200;

export default function TicketLinkPicker({
  inputId,
  excludeIds = [],
  onPick,
  disabled = false,
  placeholder = 'Ticket number or subject…',
  autoFocus = false,
}) {
  const [query, setQuery] = useState('');
  const [state, setState] = useState({ status: 'idle', rows: [], error: null });
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);

  const excluded = new Set(excludeIds.map(String));

  useEffect(() => {
    const text = query.trim();
    if (!text) {
      setState({ status: 'idle', rows: [], error: null });
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setState((prev) => ({ ...prev, status: 'loading' }));
      // `exclude` takes ONE id server-side (the ticket the box is on); the rest of the
      // exclusions — already linked, already pending — are filtered here, where the
      // list is short by construction.
      const exclude = excludeIds[0] ? `&exclude=${encodeURIComponent(excludeIds[0])}` : '';
      api
        .get(`/tickets/link-candidates?q=${encodeURIComponent(text)}${exclude}`, { signal: controller.signal })
        .then((payload) => {
          setState({ status: 'ready', rows: payload?.data ?? [], error: null });
          setHighlight(0);
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          setState({ status: 'error', rows: [], error });
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // excludeIds is read at request time; re-running the search when a link lands is
    // wasted work, the row is filtered out below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const rows = state.rows.filter((row) => !excluded.has(String(row.id)));

  function pick(row) {
    onPick?.(row);
    setQuery('');
    setState({ status: 'idle', rows: [], error: null });
    inputRef.current?.focus();
  }

  function onKeyDown(event) {
    if (!rows.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((h) => Math.min(rows.length - 1, h + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (event.key === 'Enter') {
      // Enter picks; the form's own Enter guard stops it submitting (New.jsx).
      event.preventDefault();
      pick(rows[highlight] ?? rows[0]);
    } else if (event.key === 'Escape') {
      setQuery('');
    }
  }

  const listId = inputId ? `${inputId}-options` : undefined;

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={inputId}
        className="field-input"
        type="text"
        autoComplete="off"
        role="combobox"
        aria-expanded={rows.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        placeholder={placeholder}
        value={query}
        disabled={disabled}
        autoFocus={autoFocus}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={onKeyDown}
      />
      {query.trim() && (
        <div
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-line bg-surface shadow-lg"
        >
          {state.status === 'error' && (
            <p className="px-3 py-2 text-sm text-red-700 dark:text-red-300">
              Could not search tickets. {state.error?.message}
            </p>
          )}
          {state.status !== 'error' && rows.length === 0 && (
            <p className="px-3 py-2 text-sm text-ink-muted">
              {state.status === 'loading' ? 'Searching…' : 'No matching tickets.'}
            </p>
          )}
          {rows.map((row, index) => (
            <button
              key={row.id}
              type="button"
              role="option"
              aria-selected={index === highlight}
              className={`flex w-full items-start justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-sunken ${
                index === highlight ? 'bg-sunken' : ''
              }`}
              onMouseEnter={() => setHighlight(index)}
              onClick={() => pick(row)}
            >
              <span className="min-w-0">
                <span className="font-mono text-xs text-ink-muted">#{row.public_ref}</span>
                <span className="block truncate text-ink">{row.subject}</span>
                {(row.organization_name || row.customer_name) && (
                  <span className="block truncate text-xs text-ink-muted">
                    {row.organization_name ?? row.customer_name}
                  </span>
                )}
              </span>
              <StatusBadge status={row.status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
